import { AlertTriangle, Inbox } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

/** Minimum time the loading skeleton stays on screen, to avoid a flash. */
const SKELETON_MIN_VISIBLE_MS = 200;

/**
 * Keeps `active` truthy for at least `minMs` once it has become true, so a
 * loading indicator that flips on/off faster than `minMs` does not flicker.
 */
export function useMinimumVisible(active: boolean, minMs = SKELETON_MIN_VISIBLE_MS): boolean {
  const [visible, setVisible] = useState(active);
  const shownAtRef = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      if (shownAtRef.current === null) shownAtRef.current = Date.now();
      setVisible(true);

      return undefined;
    }

    if (shownAtRef.current === null) {
      setVisible(false);

      return undefined;
    }

    const elapsed = Date.now() - shownAtRef.current;
    const remaining = minMs - elapsed;

    if (remaining <= 0) {
      shownAtRef.current = null;
      setVisible(false);

      return undefined;
    }

    const id = setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
    }, remaining);

    return () => clearTimeout(id);
  }, [active, minMs]);

  return visible;
}

/** Skeleton rows for a table — shimmer placeholders, not a spinner. */
export function TableSkeleton({
  rows = 8,
  label = 'Loading fleet',
}: {
  rows?: number;
  /** Assistive-tech announcement; override when reused outside the Fleet section. */
  label?: string;
}): JSX.Element {
  return (
    <div className="space-y-1.5" aria-label={label} role="status">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-sm" />
      ))}
    </div>
  );
}

export function EmptyState({
  message,
  action,
}: {
  message: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-secondary/40">
        <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
      <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
        {message}
      </p>
      {action}
    </div>
  );
}

export function ErrorState({
  title = "Couldn't load fleet.",
  error,
  onRetry,
  className,
}: {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): JSX.Element {
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  const requestId = error instanceof ApiError ? error.requestId : null;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border border-status-failed/30 bg-status-failed/[0.06] py-12 text-center',
        className,
      )}
      role="alert"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-status-failed/30 bg-status-failed/10">
        <AlertTriangle className="h-4 w-4 text-status-failed" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-status-failed">
          {title}
        </p>
        <p className="text-sm text-muted-foreground">{message}</p>
        {requestId && (
          <p className="mt-1 font-mono text-xs text-muted-foreground">Request ID: {requestId}</p>
        )}
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}
